export type NodeId = string

/** World coordinate shown at the viewport's top-left corner, plus scale. */
export interface Camera {
  x: number
  y: number
  z: number
}

export interface Point {
  x: number
  y: number
}

export interface Rect {
  x: number
  y: number
  w: number
  h: number
}

export interface BaseNode {
  id: NodeId
  /** World-space top-left of the unrotated bounding box. */
  x: number
  y: number
  w: number
  h: number
  /** Radians, about the bounding box center. */
  rotation: number
  /**
   * Point the rotate handle turns about, as a fraction of the unrotated box;
   * absent means the center. Relative so it follows moves and resizes.
   */
  pivot?: Point
  opacity: number
  locked: boolean
  hidden: boolean
  name?: string
  /** Members of a group share this id and are selected together on the canvas. */
  groupId?: string
}

export interface ImageNode extends BaseNode {
  type: 'image'
  assetId: string
  naturalW: number
  naturalH: number
}

export interface RectNode extends BaseNode {
  type: 'rect'
  fill: string
  stroke: string
  strokeWidth: number
  cornerRadius: number
}

export interface EllipseNode extends BaseNode {
  type: 'ellipse'
  fill: string
  stroke: string
  strokeWidth: number
}

/**
 * Lines and freehand strokes both keep their geometry as local points inside a
 * fixed viewBox of `viewW` x `viewH`. Resizing then only changes w/h and the SVG
 * stretches the viewBox for us — the points never need rewriting, so they cannot
 * drift out of step with the bounding box.
 */
export interface LineNode extends BaseNode {
  type: 'line'
  points: number[]
  /** Joins the last point back to the first. */
  closed?: boolean
  /** Painted only when closed. */
  fill?: string
  viewW: number
  viewH: number
  stroke: string
  strokeWidth: number
}

export interface PathNode extends BaseNode {
  type: 'path'
  points: number[]
  viewW: number
  viewH: number
  stroke: string
  strokeWidth: number
}

export type TextAlign = 'left' | 'center' | 'right'

export interface TextNode extends BaseNode {
  type: 'text'
  text: string
  fontSize: number
  fontFamily: string
  color: string
  align: TextAlign
  /** When true, w/h track the rendered text instead of being user-set. */
  autoWidth: boolean
}

export type SceneNode =
  | ImageNode
  | RectNode
  | EllipseNode
  | LineNode
  | PathNode
  | TextNode

export type NodeType = SceneNode['type']

export const SCENE_FORMAT_VERSION = 1

export interface Scene {
  formatVersion: number
  nodes: Record<NodeId, SceneNode>
  /** Paint order, back to front. The single source of truth for z-order. */
  order: NodeId[]
  camera: Camera
  background: string
}

export function emptyScene(): Scene {
  return {
    formatVersion: SCENE_FORMAT_VERSION,
    nodes: {},
    order: [],
    camera: { x: 0, y: 0, z: 1 },
    background: '#f6f6f4',
  }
}
